import { WebContainer } from '@webcontainer/api';

export class WebContainerService {
  private static instance: WebContainerService;
  private webcontainer: WebContainer | null = null;
  private isBooting = false;
  private bootPromise: Promise<void> | null = null;
  private currentProcess: unknown = null;
  private outputCallbacks: ((output: string) => void)[] = [];
  private previewUrl: string | null = null;
  private isFallbackMode = false;

  private constructor() {}

  static getInstance(): WebContainerService {
    if (!WebContainerService.instance) {
      WebContainerService.instance = new WebContainerService();
    }
    return WebContainerService.instance;
  }

  // Method to subscribe to output
  onOutput(callback: (output: string) => void): () => void {
    this.outputCallbacks.push(callback);
    return () => {
      const index = this.outputCallbacks.indexOf(callback);
      if (index > -1) {
        this.outputCallbacks.splice(index, 1);
      }
    };
  }

  private emitOutput(output: string) {
    this.outputCallbacks.forEach(callback => callback(output));
  }

  async boot(): Promise<void> {
    if (this.webcontainer) return;
    if (this.isBooting && this.bootPromise) return this.bootPromise;

    this.isBooting = true;
    this.bootPromise = this._boot();
    await this.bootPromise;
    this.isBooting = false;
  }

  private async _boot(): Promise<void> {
    try {
      // Check if crossOriginIsolated is available
      if (!crossOriginIsolated) {
        this.emitOutput('⚠️  WebContainer requires cross-origin isolation headers.\n');
        this.emitOutput('Please add the following headers to your server:\n');
        this.emitOutput('Cross-Origin-Embedder-Policy: require-corp\n');
        this.emitOutput('Cross-Origin-Opener-Policy: same-origin\n');
        this.emitOutput('🔄 Falling back to static preview mode.\n');
        this.isFallbackMode = true;
        return;
      }

      this.webcontainer = await WebContainer.boot();
      
      // Mount the file system with a basic React setup
      await this.webcontainer.mount({
        'package.json': {
          file: {
            contents: JSON.stringify({
              name: 'react-preview',
              type: 'module',
              dependencies: {
                react: '^18.3.1',
                'react-dom': '^18.3.1',
                '@vitejs/plugin-react': '^4.3.1',
                vite: '^5.4.2'
              },
              scripts: {
                dev: 'vite --port 3000 --host'
              }
            }, null, 2)
          }
        },
        'vite.config.js': {
          file: {
            contents: `
import { defineConfig } from 'vite';
import react from '@vitejs/plugin-react';

export default defineConfig({
  plugins: [react()],
  server: {
    port: 3000,
    host: true
  }
});
            `.trim()
          }
        },
        'index.html': {
          file: {
            contents: `
<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1.0" />
    <title>React Preview</title>
    <script src="https://cdn.tailwindcss.com"></script>
  </head>
  <body>
    <div id="root"></div>
    <script type="module" src="/src/main.jsx"></script>
  </body>
</html>
            `.trim()
          }
        },
        'src': {
          directory: {
            'main.jsx': {
              file: {
                contents: `
import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
                `.trim()
              }
            },
            'App.jsx': {
              file: {
                contents: `
import React from 'react';

function App() {
  return (
    <div className="p-8 text-center">
      <h1 className="text-3xl font-bold text-blue-600 mb-4">
        Loading...
      </h1>
    </div>
  );
}

export default App;
                `.trim()
              }
            }
          }
        }
      });

      this.emitOutput('📦 Installing dependencies...\n');
      
      // Install dependencies
      const installProcess = await this.webcontainer.spawn('npm', ['install']);
      
      // Capture install output
      installProcess.output.pipeTo(new WritableStream({
        write: (data) => {
          this.emitOutput(data);
        }
      }));
      
      await installProcess.exit;
      this.emitOutput('✅ Dependencies installed successfully\n');

      // Start the React dev server
      this.emitOutput('🚀 Starting React development server...\n');
      const devProcess = await this.webcontainer.spawn('npm', ['run', 'dev']);
      
      // Capture dev server output
      devProcess.output.pipeTo(new WritableStream({
        write: (data) => {
          this.emitOutput(data);
        }
      }));
      
      // Wait for server to be ready
      this.webcontainer.on('server-ready', (port, url) => {
        this.emitOutput(`✅ Server ready at ${url}\n`);
        this.previewUrl = url;
      });

    } catch (error) {
      console.error('Failed to boot WebContainer:', error);
      this.emitOutput(`❌ Failed to boot WebContainer: ${error}\n`);
      throw error;
    }
  }

  async updateComponent(code: string): Promise<void> {
    if (!this.webcontainer) {
      throw new Error('WebContainer not initialized');
    }

    try {
      this.emitOutput('📝 Updating React component...\n');
      // Write the new component code
      await this.webcontainer.fs.writeFile('/src/App.jsx', code);
      this.emitOutput('✅ Component updated successfully\n');
    } catch (error) {
      console.error('Failed to update component:', error);
      this.emitOutput(`❌ Failed to update component: ${error}\n`);
      throw error;
    }
  }

  isReady(): boolean {
    return this.webcontainer !== null || this.isFallbackMode;
  }

  getPreviewUrl(): string | null {
    if (this.isFallbackMode) {
      return 'fallback-mode';
    }
    return this.previewUrl;
  }

  async getFileSystemTree(): Promise<FileSystemNode[]> {
    if (!this.webcontainer) {
      throw new Error('WebContainer not initialized');
    }

    try {
      const tree = await this.readDirectory('/');
      return tree;
    } catch (error) {
      console.error('Failed to read file system:', error);
      throw error;
    }
  }

  async readDirectory(path: string): Promise<FileSystemNode[]> {
    if (!this.webcontainer) {
      throw new Error('WebContainer not initialized');
    }

    try {
      const entries = await this.webcontainer.fs.readdir(path);
      const nodes: FileSystemNode[] = [];

      for (const entry of entries) {
        // Skip node_modules directory
        if (entry === 'node_modules') {
          continue;
        }

        const fullPath = path === '/' ? `/${entry}` : `${path}/${entry}`;
        
        try {
          // Try to read the entry as a file first
          await this.webcontainer.fs.readFile(fullPath);
          // If successful, it's a file
          nodes.push({
            name: entry,
            path: fullPath,
            type: 'file'
          });
        } catch {
          // If reading as file fails, it's likely a directory
          try {
            // Try to read the directory contents to confirm it's a directory
            await this.webcontainer.fs.readdir(fullPath);
            const children = await this.readDirectory(fullPath);
            nodes.push({
              name: entry,
              path: fullPath,
              type: 'directory',
              children
            });
          } catch {
            // If both fail, skip this entry
            console.warn(`Could not determine type for entry: ${fullPath}`);
          }
        }
      }

      return nodes.sort((a, b) => {
        // Directories first, then files
        if (a.type !== b.type) {
          return a.type === 'directory' ? -1 : 1;
        }
        // Alphabetical order
        return a.name.localeCompare(b.name);
      });
    } catch (error) {
      console.error(`Failed to read directory ${path}:`, error);
      throw error;
    }
  }

  async readFile(path: string): Promise<string> {
    if (!this.webcontainer) {
      throw new Error('WebContainer not initialized');
    }

    try {
      const content = await this.webcontainer.fs.readFile(path, 'utf-8');
      return content;
    } catch (error) {
      console.error(`Failed to read file ${path}:`, error);
      throw error;
    }
  }

  async writeFile(path: string, content: string): Promise<void> {
    if (!this.webcontainer) {
      throw new Error('WebContainer not initialized');
    }

    try {
      await this.webcontainer.fs.writeFile(path, content);
    } catch (error) {
      console.error(`Failed to write file ${path}:`, error);
      throw error;
    }
  }

  async createDirectory(path: string): Promise<void> {
    if (!this.webcontainer) {
      throw new Error('WebContainer not initialized');
    }

    try {
      // WebContainer doesn't have a direct mkdir method, so we'll create a temporary file
      // and then remove it to create the directory
      const tempFile = `${path}/.temp`;
      await this.webcontainer.fs.writeFile(tempFile, '');
      await this.webcontainer.fs.rm(tempFile);
    } catch (error) {
      console.error(`Failed to create directory ${path}:`, error);
      throw error;
    }
  }

  async deleteFile(path: string): Promise<void> {
    if (!this.webcontainer) {
      throw new Error('WebContainer not initialized');
    }

    try {
      await this.webcontainer.fs.rm(path);
    } catch (error) {
      console.error(`Failed to delete file ${path}:`, error);
      throw error;
    }
  }

  async refreshFileSystem(): Promise<void> {
    if (!this.webcontainer) {
      throw new Error('WebContainer not initialized');
    }

    try {
      // Force a refresh by re-reading the file system
      await this.getFileSystemTree();
    } catch (error) {
      console.error('Failed to refresh file system:', error);
      throw error;
    }
  }
}

export interface FileSystemNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: FileSystemNode[];
}